// Client-side image compression run BEFORE every upload (feed / stories / chat /
// church covers / ads / marketplace…). Goal: uploads are faster and lighter.
//
// Design rules (must never regress):
//  • OOM-safe: never fully decode a huge photo (e.g. 108MP Xiaomi → ~432 MB).
//    We use createImageBitmap with a SCALED decode. Falls back to <img> on old WebViews.
//  • Keep transparency: PNG/WebP stay PNG/WebP (avatars, logos, stickers). Only
//    photographic formats (JPEG/HEIC…) become JPEG.
//  • Correct rotation: apply EXIF orientation (imageOrientation:'from-image') so
//    a re-encoded photo is never sideways. max(w,h) is invariant under 90° rotation,
//    so we can size from the header AND stay correctly oriented.
//  • Never enlarge: we downscale only (no upscaling) and keep the ORIGINAL file
//    whenever the re-encode isn't actually smaller.
//  • GIF is left untouched (preserve animation).

const DEFAULT_MAX_DIM = 1600;
const JPEG_QUALITY = 0.82;
const WEBP_QUALITY = 0.85;
// Skip work entirely for images that are already small AND within bounds.
const SKIP_BYTES = 600 * 1024;

const outTypeFor = (file) => {
  const t = (file.type || "").toLowerCase();
  if (t === "image/png") return "image/png";
  if (t === "image/webp") return "image/webp";
  return "image/jpeg"; // jpeg, heic, heif, bmp… → jpeg
};

const extFor = (type) => ({ "image/png": ".png", "image/webp": ".webp", "image/jpeg": ".jpg" }[type] || ".jpg");

const renameTo = (file, type) =>
  (file.name || "photo").replace(/\.\w+$/, "") + extFor(type);

// Parse intrinsic pixel size from the file header (no pixel allocation → cheap & safe).
// Returns {w,h} or null. Reads only the first 64 KB.
export const readImageSize = async (file) => {
  try {
    const ab = await file.slice(0, 65536).arrayBuffer();
    const v = new DataView(ab);
    const n = v.byteLength;
    if (n < 12) return null;
    // PNG
    if (v.getUint32(0) === 0x89504e47) return { w: v.getUint32(16), h: v.getUint32(20) };
    // GIF
    if (v.getUint8(0) === 0x47 && v.getUint8(1) === 0x49 && v.getUint8(2) === 0x46)
      return { w: v.getUint16(6, true), h: v.getUint16(8, true) };
    // WebP (RIFF....WEBP)
    if (v.getUint32(0) === 0x52494646 && v.getUint32(8) === 0x57454250 && n >= 30) {
      const fmt = v.getUint32(12);
      if (fmt === 0x56503858) { // 'VP8X'
        const w = 1 + (v.getUint8(24) | (v.getUint8(25) << 8) | (v.getUint8(26) << 16));
        const h = 1 + (v.getUint8(27) | (v.getUint8(28) << 8) | (v.getUint8(29) << 16));
        return { w, h };
      }
      if (fmt === 0x56503820) { // 'VP8 ' (lossy)
        return { w: v.getUint16(26, true) & 0x3fff, h: v.getUint16(28, true) & 0x3fff };
      }
      if (fmt === 0x5650384c) { // 'VP8L' (lossless)
        const b0 = v.getUint8(21), b1 = v.getUint8(22), b2 = v.getUint8(23), b3 = v.getUint8(24);
        return { w: 1 + (((b1 & 0x3f) << 8) | b0), h: 1 + (((b3 & 0x0f) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6)) };
      }
    }
    // JPEG — scan segments for a Start-Of-Frame marker.
    if (v.getUint16(0) === 0xffd8) {
      let off = 2;
      while (off + 9 < n) {
        if (v.getUint8(off) !== 0xff) { off += 1; continue; }
        const marker = v.getUint8(off + 1);
        if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
          return { h: v.getUint16(off + 5), w: v.getUint16(off + 7) };
        }
        const segLen = v.getUint16(off + 2);
        if (segLen < 2) break;
        off += 2 + segLen;
      }
    }
  } catch { /* noop */ }
  return null;
};

const encode = (canvas, type, quality) => new Promise((r) => canvas.toBlob(r, type, quality));

const encodeBitmap = async (bmp, file, outType) => {
  const canvas = document.createElement("canvas");
  canvas.width = bmp.width; canvas.height = bmp.height;
  canvas.getContext("2d").drawImage(bmp, 0, 0);
  try { bmp.close?.(); } catch { /* noop */ }
  const q = outType === "image/png" ? undefined : (outType === "image/webp" ? WEBP_QUALITY : JPEG_QUALITY);
  let blob = await encode(canvas, outType, q);
  if (!blob) blob = await encode(canvas, "image/jpeg", JPEG_QUALITY); // WebView that can't encode png/webp
  if (!blob || blob.size >= file.size) return file; // no real gain → keep original bytes
  const type = blob.type || outType;
  return new File([blob], renameTo(file, type), { type, lastModified: Date.now() });
};

/**
 * Compress an image File before upload. Returns a (usually smaller) File, or the
 * original untouched when compression wouldn't help / isn't possible.
 */
export const compressImageFile = async (file, { maxDim = DEFAULT_MAX_DIM } = {}) => {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // keep animation

  const outType = outTypeFor(file);
  const size = await readImageSize(file);
  const longSide = size ? Math.max(size.w, size.h) : 0;

  // Already small and within bounds → nothing to gain.
  if (size && longSide > 0 && longSide <= maxDim && file.size <= SKIP_BYTES) return file;

  if (typeof createImageBitmap === "function") {
    try {
      // Probe (tiny, oriented) → cheap aspect ratio without a full-res allocation.
      const probe = await createImageBitmap(file, { imageOrientation: "from-image", resizeWidth: 100, resizeQuality: "low" });
      const aspect = probe.width / Math.max(1, probe.height); // oriented aspect
      try { probe.close?.(); } catch { /* noop */ }

      // Target long side: cap at maxDim, and never upscale past the source.
      const targetLong = longSide > 0 ? Math.min(maxDim, longSide) : maxDim;
      let tw, th;
      if (aspect >= 1) { tw = targetLong; th = Math.max(1, Math.round(targetLong / aspect)); }
      else { th = targetLong; tw = Math.max(1, Math.round(targetLong * aspect)); }

      const bmp = await createImageBitmap(file, {
        imageOrientation: "from-image",
        resizeWidth: tw, resizeHeight: th, resizeQuality: "high",
      });
      return await encodeBitmap(bmp, file, outType);
    } catch { /* fall through to legacy path */ }
  }

  // Legacy fallback (<img> decode; the browser applies EXIF orientation itself).
  return await new Promise((resolve) => {
    const objUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      let { width, height } = img;
      const long = Math.max(width, height);
      if (long > maxDim) {
        if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else { width = Math.round((width * maxDim) / height); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      const q = outType === "image/png" ? undefined : (outType === "image/webp" ? WEBP_QUALITY : JPEG_QUALITY);
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        const type = blob.type || outType;
        resolve(new File([blob], renameTo(file, type), { type, lastModified: Date.now() }));
      }, outType, q);
    };
    img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(file); };
    img.src = objUrl;
  });
};

export default compressImageFile;
